import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { RootState } from "../../app/store"
import { ActionWrapper } from "../../lib/api-status/actionWrapper"
import { getError } from "../../lib/api-status/errorHandler"
import { authenticate, verifyMfa } from "./authenticationApi"
import { FetchStatus, IStatus } from '../../lib/api-status/IStatus'
import { Password } from "@mui/icons-material"

type Password = {
    ciphertext: String | undefined,
    salt: String | undefined,
    iv: String | undefined,
}

export type MfaPendingState = {
    challengeId: string
    maskedEmail?: string | null
    expiresInSeconds?: number | null
}

type InitialState = {
    token: String | undefined,
    activeRole: string | undefined,
    availableRoles: string[],
    response: string | undefined,
    status: IStatus,
    actionStatus: IStatus,
    mfaPending: MfaPendingState | undefined,
}
const initialState: InitialState = {
    token: undefined,
    activeRole: undefined,
    availableRoles: [],
    response: undefined,
    status: { fetchStatus: FetchStatus.IDLE },
    actionStatus: { fetchStatus: FetchStatus.IDLE },
    mfaPending: undefined,
}

export interface ILoginRequest {
    username: String | undefined,
    password: String | undefined,
    passwordWithSaltAndIv: Password | undefined,
    captchaResponse: String | undefined,
}

export const defaultLoginRequest = {
    username: undefined,
    passwordWithSaltAndIv: undefined,
    password: undefined,
    captchaResponse: undefined,
}

export interface ILoginResponse {
    mfaRequired?: boolean
    currentUser?: string
    challengeId?: string | null
    expiresInSeconds?: number | null
    maskedEmail?: string | null
    refreshToken?: string | null
}

interface IMfaVerifyResponse {
    currentUser: string
    refreshToken?: string | null
}

function decodeRolesFromToken(token: string | undefined): string[] {
  if (!token) return [];
  try {
    const payloadPart = token.split(".")[1];
    if (!payloadPart) return [];
    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const payload = JSON.parse(window.atob(padded));
    const roles = payload?.rol;
    let parsed: string[] = [];
    if (Array.isArray(roles)) {
      parsed = roles.map((r) => String(r).toUpperCase());
    } else if (typeof roles === "string") {
      parsed = roles.split(",").map((r: string) => r.trim().toUpperCase()).filter(Boolean);
    }
    // Normalize legacy MANAGER → USERADMIN
    if (parsed.includes("MANAGER")) {
      parsed = parsed.map((r) => r === "MANAGER" ? "USERADMIN" : r);
    }
    // If user has both CHECKER and USERADMIN (or legacy CHECKER+MANAGER),
    // show as two separate options: CHECKER (checker dashboard) and USERADMIN (admin page)
    if (parsed.includes("CHECKER") && parsed.includes("USERADMIN")) {
      return ["CHECKER", "USERADMIN"];
    }
    return parsed;
  } catch {
    return [];
  }
}

export const authenticateThunk = createAsyncThunk(
  'auth/authenticate',
  //First argument of void is needed to inject rejectWithValue
  async (args: ActionWrapper<ILoginRequest>, {rejectWithValue}) => { 
    try {
      if(args.argument) {
        const response = await authenticate(args.argument);
        return response.data as ILoginResponse;
      }
    } catch(reason) {
      // Security: Log error without exposing sensitive authentication data
      console.log("Authentication error occurred");
      return rejectWithValue(getError(reason));
    }
  }
);

export const verifyMfaThunk = createAsyncThunk(
  'auth/verifyMfa',
  async (args: ActionWrapper<{ challengeId: string; otp: string }>, { rejectWithValue }) => {
    try {
      if (!args.argument) {
        return rejectWithValue({ message: 'Missing verification data' });
      }
      const response = await verifyMfa(args.argument);
      return response.data as IMfaVerifyResponse;
    } catch (reason) {
      return rejectWithValue(getError(reason));
    }
  }
);

const authticationSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    clearErrorMessage:  (state) => {
      state.status.fetchStatus = FetchStatus.IDLE;
    },
    clearMfaPending: (state) => {
      state.mfaPending = undefined;
    },
    setErrorMessage:  (state, action: PayloadAction<string>) => {
      state.response = action.payload
      state.status.fetchStatus = FetchStatus.FAILED
    },
    setActiveRole: (state, action: PayloadAction<string>) => {
      state.activeRole = action.payload;
      localStorage.setItem("activeRole", action.payload);
    },
    setSessionTokens: (state, action: PayloadAction<{ token: string; refreshToken?: string }>) => {
      state.token = action.payload.token;
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('login_timestamp', Date.now().toString());
      if (action.payload.refreshToken) {
        localStorage.setItem('refreshToken', action.payload.refreshToken);
      }
      state.availableRoles = decodeRolesFromToken(action.payload.token);
    }
  },
  extraReducers: builder => {
    builder.addCase(authenticateThunk.pending, state => {
      state.status.fetchStatus = FetchStatus.DOING;
    })
    .addCase(
        authenticateThunk.fulfilled,
      (state, action: PayloadAction<ILoginResponse | undefined>) => {
        const payload = action.payload;
        // Security: Removed token logging - console.log(payload);
        if (payload?.mfaRequired === true && payload.challengeId) {
          state.mfaPending = {
            challengeId: payload.challengeId,
            maskedEmail: payload.maskedEmail,
            expiresInSeconds: payload.expiresInSeconds,
          };
          state.response = undefined;
          state.status.fetchStatus = FetchStatus.IDLE;
          return;
        }
        if (payload?.currentUser) {
          state.token = payload.currentUser;
          state.availableRoles = decodeRolesFromToken(payload.currentUser);
          if (state.availableRoles.length === 1) {
            state.activeRole = state.availableRoles[0];
            localStorage.setItem("activeRole", state.availableRoles[0]);
          } else {
            state.activeRole = undefined;
            localStorage.removeItem("activeRole");
          }
          state.mfaPending = undefined;
          // Security: Safe logging without exposing tokens
          console.log("Authentication successful - User roles:", state.availableRoles);
          state.response = undefined;
          localStorage.setItem('token', String(state.token));
          localStorage.setItem('login_timestamp', Date.now().toString());
          const tabId = sessionStorage.getItem('app_tab_id');
          if (tabId) localStorage.setItem('active_tab_id', tabId);
          if (payload.refreshToken) {
            localStorage.setItem('refreshToken', String(payload.refreshToken));
          }
        }
        state.status.fetchStatus = FetchStatus.IDLE;
      }
    )
    .addCase(verifyMfaThunk.pending, (state) => {
      state.status.fetchStatus = FetchStatus.DOING;
    })
    .addCase(verifyMfaThunk.fulfilled, (state, action: PayloadAction<IMfaVerifyResponse | undefined>) => {
      const token = action.payload?.currentUser;
      if (token) {
        state.token = token;
        state.availableRoles = decodeRolesFromToken(token);
        if (state.availableRoles.length === 1) {
          state.activeRole = state.availableRoles[0];
          localStorage.setItem("activeRole", state.availableRoles[0]);
        } else {
          state.activeRole = undefined;
          localStorage.removeItem("activeRole");
        }
        state.mfaPending = undefined;
        state.response = undefined;
        localStorage.setItem('token', String(state.token));
        localStorage.setItem('login_timestamp', Date.now().toString());
        const tabId = sessionStorage.getItem('app_tab_id');
        if (tabId) localStorage.setItem('active_tab_id', tabId);
        const refresh = action.payload?.refreshToken;
        if (refresh) {
          localStorage.setItem('refreshToken', String(refresh));
        }
      }
      state.status.fetchStatus = FetchStatus.IDLE;
    })
    .addCase(verifyMfaThunk.rejected, (state, action: any) => {
      state.response = action.payload?.message;
      state.status.fetchStatus = FetchStatus.FAILED;
    })
    .addCase(authenticateThunk.rejected, (state, action: any) => {
      // let errStr = "unknown error. please contact support"
      // const errOut = "Invalid Username / Password entered. Try again!"
      // state.response = action.payload?.message ? action.payload?.message.toLowerCase() == errStr ? errOut : action.payload?.message: "Error Contact Support";
      // Security: Log error without exposing sensitive payload data
      console.log("Authentication failed:", action.payload?.message || "Unknown error");
      state.response = action.payload.message;
      state.status.fetchStatus = FetchStatus.FAILED;
    })
  }
})

export default authticationSlice.reducer
export const { setErrorMessage, clearErrorMessage, clearMfaPending, setActiveRole, setSessionTokens } = authticationSlice.actions

export const selectAuthenticatedUser = (state: RootState) => state.auth;
export const selectMfaPending = (state: RootState) => state.auth.mfaPending;